import axios from "axios";
import { getSessionId } from "../helpers/session";

const DEFAULT_BASE_URL =
  process.env.NODE_ENV === "production"
    ? "https://dragonkind-server-mhfe4.ondigitalocean.app"
    : "http://localhost:3001";

export const BASE_URL = (
  process.env.REACT_APP_BASE_URL || DEFAULT_BASE_URL
).replace(/\/+$/, "");

const getStoredValue = (key) => {
  if (typeof window === "undefined") {
    return "";
  }

  try {
    return window.localStorage.getItem(key) || "";
  } catch (error) {
    console.error(`Unable to read localStorage key "${key}":`, error);
    return "";
  }
};

class Api {
  static token = "";

  static setToken(newToken = "") {
    this.token = newToken;
  }

  static clearToken() {
    this.token = "";
  }

  static async request(endpoint, data = {}, method = "get") {
    const normalizedEndpoint = String(endpoint).replace(/^\/+/, "");
    const normalizedMethod = String(method).toLowerCase();
    const url = `${BASE_URL}/${normalizedEndpoint}`;

    const headers = this.token
      ? {
          Authorization: `Dragonian ${this.token}`,
        }
      : {};

    const requestConfig = {
      url,
      method: normalizedMethod,
      headers,
    };

    if (normalizedMethod === "get") {
      requestConfig.params = data;
    } else {
      requestConfig.data = data;
    }

    try {
      const response = await axios(requestConfig);
      return response.data;
    } catch (error) {
      console.error("API request failed:", {
        endpoint: normalizedEndpoint,
        method: normalizedMethod,
        status: error?.response?.status,
        response: error?.response?.data,
        message: error?.message,
      });

      const message =
        error?.response?.data?.error?.message ||
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        "The request could not be completed.";

      const normalizedMessage = Array.isArray(message)
        ? message.join(" ")
        : String(message);

      const requestError = new Error(normalizedMessage);
      requestError.status = error?.response?.status;
      requestError.data = error?.response?.data;
      requestError.endpoint = normalizedEndpoint;
      requestError.method = normalizedMethod;

      throw requestError;
    }
  }

  /* -----------------------------------------
     Public and Read-Only
  ------------------------------------------ */

  static getAllAuthor(entity) {
    return this.request(entity);
  }

  static getEnvVars() {
    return this.request("envVars");
  }

  static getByTitleOrUsername(entity, title) {
    return this.request(
      `${entity}/${encodeURIComponent(title)}`
    );
  }

  static getByTitleId(entity, id) {
    return this.request(
      `${entity}/id/${encodeURIComponent(id)}`
    );
  }

  static getByTitleUserId(entity, id) {
    return this.request(
      `${entity}/user/${encodeURIComponent(id)}`
    );
  }

  static getFeaturedBook() {
    return this.request("books/featured");
  }

  static getLatestNews(limit = 3) {
    return this.request("news", { limit });
  }

  static subscribeToNewsletter(email) {
    return this.request(
      "newsletter/subscribe",
      { email },
      "post"
    );
  }

  static requestReaderMagnet(email, format, analytics) {
    return this.request("newsletter/reader-magnet/request", { email, format, consent: true, analytics }, "post");
  }

  static unsubscribeNewsletter(email) {
    return this.request(
      "newsletter/unsubscribe",
      { email },
      "post"
    );
  }

  /* -----------------------------------------
     Newsletter Templates and Delivery
  ------------------------------------------ */

  static getNewsletterTemplates() {
    return this.request("newsletter/templates");
  }

  static saveEmailTemplate(data) {
    return this.request(
      "newsletter/templates",
      data,
      "post"
    );
  }

  static activateEmailTemplate(data) {
    return this.request(
      "newsletter/templates/activate",
      data,
      "post"
    );
  }

  static scheduleNewsletter(data) {
    return this.request(
      "newsletter/schedule",
      data,
      "post"
    );
  }

  static sendTestNewsletter(data) {
    return this.request(
      "newsletter/send-test",
      data,
      "post"
    );
  }

  static sendNewsletter(data) {
    return this.request(
      "newsletter/send",
      data,
      "post"
    );
  }

  static getNewsletterSubscribers() {
    return this.request("newsletter");
  }

  static getNewsletterHistory(page = 1, pageSize = 10) {
    return this.request("newsletter/history", {
      page,
      pageSize,
    });
  }

  static getNewsletterById(id) {
    return this.request(
      `newsletter/history/${encodeURIComponent(id)}`
    );
  }

  static resendNewsletter(id) {
    return this.request(
      `newsletter/history/${encodeURIComponent(id)}/resend`,
      {},
      "post"
    );
  }

  static deleteNewsletterRecord(id) {
    return this.request(
      `newsletter/history/${encodeURIComponent(id)}`,
      {},
      "delete"
    );
  }

  static pruneInactiveSubscribers() {
    return this.request(
      "newsletter/subscribers/cleanup",
      {},
      "post"
    );
  }

  static deleteNewsletterSubscriber(id) {
    return this.request(
      `newsletter/subscribers/${encodeURIComponent(id)}`,
      {},
      "delete"
    );
  }

  /* -----------------------------------------
     General Administrative CRUD
  ------------------------------------------ */

  static createAuthor(endpoint, data) {
    return this.request(endpoint, data, "post");
  }

  static updateAuthor(endpoint, id, data) {
    return this.request(
      `${endpoint}/${encodeURIComponent(id)}`,
      data,
      "patch"
    );
  }

  static deleteAuthor(endpoint, id) {
    return this.request(
      `${endpoint}/${encodeURIComponent(id)}`,
      {},
      "delete"
    );
  }

  /* -----------------------------------------
     Books
  ------------------------------------------ */

  static createBook(data) {
    return this.request("books", data, "post");
  }

  static updateBook(title, data) {
    return this.request(
      `books/${encodeURIComponent(title)}`,
      data,
      "patch"
    );
  }

  static uploadBookFiles(title, formData) {
    return this.request(
      `books/${encodeURIComponent(title)}/ebook-files`,
      formData,
      "post"
    );
  }

  static deleteBook(title) {
    return this.request(
      `books/${encodeURIComponent(title)}`,
      {},
      "delete"
    );
  }

  static createBookReview(data) {
    return this.request(
      "bookReviews",
      data,
      "post"
    );
  }

  static updateBookReview(id, data) {
    return this.request(
      `bookReviews/${encodeURIComponent(id)}`,
      data,
      "patch"
    );
  }

  static deleteBookReview(id) {
    return this.request(
      `bookReviews/${encodeURIComponent(id)}`,
      {},
      "delete"
    );
  }

  /* -----------------------------------------
     Blogs
  ------------------------------------------ */

  static createBlog(data) {
    return this.request("blog", data, "post");
  }

  static updateBlog(title, data) {
    return this.request(
      `blog/${encodeURIComponent(title)}`,
      data,
      "patch"
    );
  }

  static deleteBlog(title) {
    return this.request(
      `blog/${encodeURIComponent(title)}`,
      {},
      "delete"
    );
  }

  static createBlogComment(data) {
    return this.request(
      "blog/comments",
      data,
      "post"
    );
  }

  static updateBlogComment(id, data) {
    return this.request(
      `blog/comments/${encodeURIComponent(id)}`,
      data,
      "patch"
    );
  }

  /* -----------------------------------------
     Characters, Races, and Words
  ------------------------------------------ */

  static createCharacter(data) {
    return this.request(
      "characters",
      data,
      "post"
    );
  }

  static updateCharacter(id, data) {
    return this.request(
      `characters/${encodeURIComponent(id)}`,
      data,
      "patch"
    );
  }

  static deleteCharacter(id) {
    return this.request(
      `characters/${encodeURIComponent(id)}`,
      {},
      "delete"
    );
  }

  static createRace(data) {
    return this.request("races", data, "post");
  }

  static updateRace(id, data) {
    return this.request(
      `races/${encodeURIComponent(id)}`,
      data,
      "patch"
    );
  }

  static deleteRace(id) {
    return this.request(
      `races/${encodeURIComponent(id)}`,
      {},
      "delete"
    );
  }

  static createWord(data) {
    return this.request("words", data, "post");
  }

  static updateWord(id, data) {
    return this.request(
      `words/${encodeURIComponent(id)}`,
      data,
      "patch"
    );
  }

  static deleteWord(id) {
    return this.request(
      `words/${encodeURIComponent(id)}`,
      {},
      "delete"
    );
  }

  /* -----------------------------------------
     Amazon Rankings
  ------------------------------------------ */

  static getAllRankings() {
    return this.request("api/rankings");
  }

  static getRankingsByBook(title) {
    return this.request(
      `api/rankings/${encodeURIComponent(title)}`
    );
  }

  static getRankingsByMarketplace(marketplace) {
    return this.request(
      `api/rankings/marketplace/${encodeURIComponent(
        marketplace
      )}`
    );
  }

  static getRankingsByFormat(format) {
    return this.request(
      `api/rankings/format/${encodeURIComponent(format)}`
    );
  }

  static getRecentRankings(limit = 50) {
    return this.request("api/rankings/recent", {
      limit,
    });
  }

  static deleteRanking(id) {
    return this.request(
      `api/rankings/${encodeURIComponent(id)}`,
      {},
      "delete"
    );
  }

  static addManualRanking(data) {
    return this.request(
      "api/rankings/manual",
      data,
      "post"
    );
  }

  /* -----------------------------------------
     Social Posts
  ------------------------------------------ */

  static postToFacebook(id) {
    return this.request(
      "api/social/facebook/post-now",
      { id },
      "post"
    );
  }

  static postToInstagram(id) {
    return this.request(
      "api/social/instagram/post-now",
      { id },
      "post"
    );
  }

  static postToTwitter(id) {
    return this.request(
      "api/social/twitter/post-now",
      { id },
      "post"
    );
  }

  static createSocialPost(data) {
    return this.request(
      "social-posts",
      data,
      "post"
    );
  }

  /* -----------------------------------------
     Image Upload and Library
  ------------------------------------------ */

  static uploadImage(formData) {
    return this.request(
      "api/upload-image",
      formData,
      "post"
    );
  }

  static importRemoteImage(payload) {
    return this.request(
      "api/import-image-from-url",
      payload,
      "post"
    );
  }

  static getImages(params = {}) {
    return this.request("api/images", params);
  }

  static getImageUsage(id) {
    return this.request(
      `api/images/${encodeURIComponent(id)}/usage`
    );
  }

  static deleteImage(id, replacement_url = "") {
    return this.request(
      `api/images/${encodeURIComponent(id)}`,
      { replacement_url },
      "delete"
    );
  }

  /* -----------------------------------------
     Orders
  ------------------------------------------ */

  static getAllOrders() {
    return this.request("api/orders/all-orders");
  }

  static updateBookOrderTracking(orderNumber, data) {
    return this.request(
      "api/book-orders/tracking",
      {
        ...data,
        order_number:
          data?.order_number || orderNumber,
      },
      "post"
    );
  }

  /* -----------------------------------------
     Merchandise
  ------------------------------------------ */

  static getMerchProducts() {
    return this.request("api/merch/products");
  }

  static getMerchVariants(productId) {
    return this.request(
      `api/merch/variants/${encodeURIComponent(productId)}`
    );
  }

  static createMerchProduct(data) {
    return this.request(
      "api/merch/add",
      data,
      "post"
    );
  }

  static updateMerchProduct(id, data) {
    return this.request(
      `api/merch/update/${encodeURIComponent(id)}`,
      data,
      "put"
    );
  }

  static deleteMerchProduct(id) {
    return this.request(
      `api/merch/delete/${encodeURIComponent(id)}`,
      {},
      "delete"
    );
  }

  static updateMerchVariantPrice(
    variant_id,
    new_price
  ) {
    return this.request(
      "api/merch/variants/update",
      {
        variant_id,
        new_price,
      },
      "patch"
    );
  }

  static syncMerchProducts() {
    return this.request(
      "api/merch/sync",
      {},
      "post"
    );
  }

  static toggleMerchProductVisibility(id, is_active) {
    return this.request(
      "api/merch/visibility",
      {
        id,
        is_active,
      },
      "patch"
    );
  }

  static getAllMerchProducts() {
    return this.request("api/merch/all");
  }

  /* -----------------------------------------
     Cart
  ------------------------------------------ */

  static addToCart({
    item_type,
    sessionId,
    product_id,
    variant_id,
    variant_title,
    quantity,
    price_at_time,
    image_url,
    merch_product_id,
    merch_variant_id,
  }) {
    return this.request(
      "api/cart/add",
      {
        item_type,
        session_id: sessionId,
        product_id,
        variant_id,
        variant_title,
        quantity,
        price_at_time,
        image_url,
        merch_product_id,
        merch_variant_id,
      },
      "post"
    );
  }

  static addBookToCart({
    title,
    format,
    price,
    image_url,
  }) {
    const sessionId = getSessionId();

    return this.request(
      "api/cart/addBook",
      {
        session_id: sessionId,
        title,
        format,
        price,
        image_url,
      },
      "post"
    );
  }

  static clearCart(sessionId = getSessionId()) {
    return this.request(
      "api/cart/clear",
      { sessionId },
      "delete"
    );
  }

  static removeFromCart(id) {
    return this.request(
      `api/cart/remove/${encodeURIComponent(id)}`,
      {},
      "delete"
    );
  }

  static updateCartItemQty(id, quantity) {
    return this.request(
      "api/cart/updateQty",
      {
        id,
        quantity,
      },
      "post"
    );
  }

  static applyDiscountToCart(sessionId, code) {
    return this.request(
      "api/cart/apply-discount",
      {
        sessionId,
        code,
      },
      "post"
    );
  }

  static getCartItems() {
    return this.request("api/cart", {
      sessionId: getSessionId(),
    });
  }

  /* -----------------------------------------
     Stripe Checkout
  ------------------------------------------ */

  static createCheckoutSession(items) {
    return this.request(
      "api/stripe/create-checkout-session",
      {
        session_id: getSessionId(),
        items,
      },
      "post"
    );
  }

  static getCheckoutStatus(sessionId) {
    return this.request("api/stripe/checkout-status", {
      session_id: sessionId,
    });
  }

  static createBookCheckoutSession(
    title,
    format,
    email,
    code = ""
  ) {
    return this.request(
      "api/stripe/book-checkout",
      {
        title,
        format,
        email,
        code,
      },
      "post"
    );
  }

  /* -----------------------------------------
     Sales
  ------------------------------------------ */

  static uploadSalesCSV(formData) {
    return this.request(
      "api/sales/upload",
      formData,
      "post"
    );
  }

  static getBookSales() {
    return this.request("api/sales/books");
  }

  static getMerchSales() {
    return this.request("api/sales/merch");
  }

  static getSalesOverview() {
    return this.request("api/sales/overview");
  }

  static getSalesSummary() {
    return this.request("api/sales/summary");
  }

  static getSalesBatches() {
    return this.request("api/sales/batches");
  }

  static deleteSalesBatch(batchId) {
    return this.request(
      `api/sales/batch/${encodeURIComponent(batchId)}`,
      {},
      "delete"
    );
  }

  /* -----------------------------------------
     Cron Jobs
  ------------------------------------------ */

  static getCronJobs() {
    return this.request("api/cron-jobs");
  }

  static toggleCronJob(id) {
    return this.request(
      `api/cron-jobs/${encodeURIComponent(id)}/toggle`,
      {},
      "post"
    );
  }

  static runCronJobNow(id) {
    return this.request(
      `api/cron-jobs/${encodeURIComponent(id)}/run`,
      {},
      "post"
    );
  }

  /* -----------------------------------------
     Settings
  ------------------------------------------ */

  static getSettings() {
    return this.request("api/settings");
  }

  static updateSettings(data) {
    return this.request(
      "api/settings",
      data,
      "patch"
    );
  }

  /* -----------------------------------------
     Automated Social Posts
  ------------------------------------------ */

  static getAllAutoPosts() {
    return this.request("api/auto-posts");
  }

  static getTodayPlannedAutoPost() {
    return this.request(
      "api/auto-posts/planned/today"
    );
  }

  static getAutoPost(id) {
    return this.request(
      `api/auto-posts/${encodeURIComponent(id)}`
    );
  }

  static createAutoPost(data) {
    return this.request(
      "api/auto-posts",
      data,
      "post"
    );
  }

  static updateAutoPost(id, data) {
    return this.request(
      `api/auto-posts/${encodeURIComponent(id)}`,
      data,
      "put"
    );
  }

  static toggleAutoPostActive(id) {
    return this.request(
      `api/auto-posts/${encodeURIComponent(
        id
      )}/toggle-active`,
      {},
      "put"
    );
  }

  static toggleAutoPostPosted(id) {
    return this.request(
      `api/auto-posts/${encodeURIComponent(
        id
      )}/toggle-posted`,
      {},
      "put"
    );
  }

  static resetAllAutoPostsPosted() {
    return this.request(
      "api/auto-posts/reset/posted/all",
      {},
      "put"
    );
  }

  static deleteAutoPost(id) {
    return this.request(
      `api/auto-posts/${encodeURIComponent(id)}`,
      {},
      "delete"
    );
  }

  /* -----------------------------------------
     Email Template Management
  ------------------------------------------ */

  static getAllEmailTemplates() {
    return this.request("api/email-templates");
  }

  static getEmailTemplate(name) {
    return this.request(
      `api/email-templates/${encodeURIComponent(name)}`
    );
  }

  static createEmailTemplate(data) {
    return this.request(
      "api/email-templates",
      data,
      "post"
    );
  }

  static updateEmailTemplate(name, data) {
    return this.request(
      `api/email-templates/${encodeURIComponent(name)}`,
      data,
      "put"
    );
  }

  static toggleEmailTemplate(name, is_active) {
    return this.request(
      "api/email-templates/toggle",
      {
        template_name: name,
        is_active,
      },
      "post"
    );
  }

/* -----------------------------------------
   Testimonials
------------------------------------------ */

static getTestimonials() {
  return this.request("api/testimonials");
}

static createTestimonial(data) {
  return this.request(
    "api/testimonials",
    data,
    "post"
  );
}

static updateTestimonial(id, data) {
  return this.request(
    `api/testimonials/${encodeURIComponent(id)}`,
    data,
    "patch"
  );
}

static deleteTestimonial(id) {
  return this.request(
    `api/testimonials/${encodeURIComponent(id)}`,
    {},
    "delete"
  );
}

  /* -----------------------------------------
     Coupons
  ------------------------------------------ */

  static getCoupons() {
    return this.request("api/coupons");
  }

  static addCoupon(data) {
    return this.request(
      "api/coupons/add",
      data,
      "post"
    );
  }

  static updateCoupon(id, data) {
    return this.request(
      `api/coupons/update/${encodeURIComponent(id)}`,
      data,
      "put"
    );
  }

  static toggleCoupon(id) {
    return this.request(
      `api/coupons/toggle/${encodeURIComponent(id)}`,
      {},
      "put"
    );
  }

  static deleteCoupon(id) {
    return this.request(
      `api/coupons/${encodeURIComponent(id)}`,
      {},
      "delete"
    );
  }

    /* =========================================
     Avwakin Development
  ========================================== */

  /* -----------------------------------------
     Development Clients
  ------------------------------------------ */

  static getDevelopmentClients(params = {}) {
    return this.request("api/development/clients", params);
  }

  static getDevelopmentClient(id) {
    return this.request(
      `api/development/clients/${encodeURIComponent(id)}`
    );
  }

  static createDevelopmentClient(data) {
    return this.request(
      "api/development/clients",
      data,
      "post"
    );
  }

  static updateDevelopmentClient(id, data) {
    return this.request(
      `api/development/clients/${encodeURIComponent(id)}`,
      data,
      "patch"
    );
  }

  static deleteDevelopmentClient(id) {
    return this.request(
      `api/development/clients/${encodeURIComponent(id)}`,
      {},
      "delete"
    );
  }

  /* -----------------------------------------
     Development Services
  ------------------------------------------ */

  static getDevelopmentServices(params = {}) {
    return this.request(
      "api/development/services",
      params
    );
  }

  static getDevelopmentService(id) {
    return this.request(
      `api/development/services/${encodeURIComponent(id)}`
    );
  }

  static createDevelopmentService(data) {
    return this.request(
      "api/development/services",
      data,
      "post"
    );
  }

  static updateDevelopmentService(id, data) {
    return this.request(
      `api/development/services/${encodeURIComponent(id)}`,
      data,
      "patch"
    );
  }

  static deleteDevelopmentService(id) {
    return this.request(
      `api/development/services/${encodeURIComponent(id)}`,
      {},
      "delete"
    );
  }

  /* -----------------------------------------
     Development Projects
  ------------------------------------------ */

  static getDevelopmentProjects(params = {}) {
    return this.request(
      "api/development/projects",
      params
    );
  }

  static getDevelopmentProject(id) {
    return this.request(
      `api/development/projects/${encodeURIComponent(id)}`
    );
  }

  static getDevelopmentProjectsByClient(clientId) {
    return this.request(
      `api/development/projects/client/${encodeURIComponent(clientId)}`
    );
  }

  static createDevelopmentProject(data) {
    return this.request(
      "api/development/projects",
      data,
      "post"
    );
  }

  static updateDevelopmentProject(id, data) {
    return this.request(
      `api/development/projects/${encodeURIComponent(id)}`,
      data,
      "patch"
    );
  }

  static deleteDevelopmentProject(id) {
    return this.request(
      `api/development/projects/${encodeURIComponent(id)}`,
      {},
      "delete"
    );
  }

  /* -----------------------------------------
     Development Project Items
  ------------------------------------------ */

  static getDevelopmentProjectItems(projectId) {
    return this.request(
      `api/development/project-items/project/${encodeURIComponent(projectId)}`
    );
  }

  static createDevelopmentProjectItem(projectId, data) {
    return this.request(
      `api/development/project-items/project/${encodeURIComponent(projectId)}`,
      data,
      "post"
    );
  }

  static updateDevelopmentProjectItem(id, data) {
    return this.request(
      `api/development/project-items/${encodeURIComponent(id)}`,
      data,
      "patch"
    );
  }

  static deleteDevelopmentProjectItem(id) {
    return this.request(
      `api/development/project-items/${encodeURIComponent(id)}`,
      {},
      "delete"
    );
  }

  /* -----------------------------------------
     Development Project Tasks
  ------------------------------------------ */

  static getDevelopmentProjectTaskSummary() {
    return this.request(
      "api/development/project-tasks/summary"
    );
  }

  static getEmailLogs(params = {}) {
    return this.request("api/email-logs", params);
  }

  static getBookstores(params = {}) {
    return this.request("api/bookstores", params);
  }

  static getBookstoreSummary() {
    return this.request("api/bookstores/summary");
  }

  static getBookstorePricing() {
    return this.request("api/bookstores/pricing");
  }

  static saveBookstorePricing(data) {
    return this.request("api/bookstores/pricing", data, "put");
  }

  static importBookstorePricing(file, previewToken = null) {
    const data = new FormData();
    data.append("file", file);
    if (previewToken) data.append("preview_token", previewToken);
    return this.request(`api/bookstores/pricing/import/${previewToken ? "commit" : "preview"}`, data, "post");
  }

  static getReaderDownloadNotice(notice) {
    return this.request("newsletter/reader-magnet/download-notice", { notice });
  }

  static getBookstoreBooks() {
    return this.request("api/bookstores/books");
  }

  static getBookstoreTemplates() {
    return this.request("api/bookstores/templates");
  }

  static getBookstore(id) {
    return this.request(`api/bookstores/${encodeURIComponent(id)}`);
  }

  static createBookstore(data) {
    return this.request("api/bookstores", data, "post");
  }

  static updateBookstore(id, data) {
    return this.request(`api/bookstores/${encodeURIComponent(id)}`, data, "put");
  }

  static deleteBookstore(id) {
    return this.request(`api/bookstores/${encodeURIComponent(id)}`, {}, "delete");
  }

  static addBookstoreContact(id, data) {
    return this.request(`api/bookstores/${encodeURIComponent(id)}/contacts`, data, "post");
  }

  static previewBookstoreEmail(id, templateName) {
    return this.request(`api/bookstores/${encodeURIComponent(id)}/email-preview`, { template_name: templateName }, "post");
  }

  static sendBookstoreEmail(id, data) {
    return this.request(`api/bookstores/${encodeURIComponent(id)}/email`, data, "post");
  }

  static getBookstoreOutlookDraft(id, data) {
    return this.request(`api/bookstores/${encodeURIComponent(id)}/outlook-draft`, data, "post");
  }

  static getEmailDeliveries(params = {}) {
    return this.request("api/email-delivery", params);
  }

  static getEmailLog(id) {
    return this.request(`api/email-logs/${encodeURIComponent(id)}`);
  }

  static resendEmailLog(id) {
    return this.request(
      `api/email-logs/${encodeURIComponent(id)}/resend`,
      {},
      "post"
    );
  }

  static getDevelopmentProjectTasks(projectId, params = {}) {
    return this.request(
      `api/development/project-tasks/project/${encodeURIComponent(projectId)}`,
      params
    );
  }

  static getDevelopmentProjectTask(id) {
    return this.request(
      `api/development/project-tasks/${encodeURIComponent(id)}`
    );
  }

  static createDevelopmentProjectTask(projectId, data) {
    return this.request(
      `api/development/project-tasks/project/${encodeURIComponent(projectId)}`,
      data,
      "post"
    );
  }

  static updateDevelopmentProjectTask(id, data) {
    return this.request(
      `api/development/project-tasks/${encodeURIComponent(id)}`,
      data,
      "patch"
    );
  }

  static deleteDevelopmentProjectTask(id) {
    return this.request(
      `api/development/project-tasks/${encodeURIComponent(id)}`,
      {},
      "delete"
    );
  }

  /* -----------------------------------------
     Development Notes
  ------------------------------------------ */

  static getDevelopmentNotes(params = {}) {
    return this.request(
      "api/development/notes",
      params
    );
  }

  static getDevelopmentNote(id) {
    return this.request(
      `api/development/notes/${encodeURIComponent(id)}`
    );
  }

  static getDevelopmentNotesByClient(clientId) {
    return this.request(
      `api/development/notes/client/${encodeURIComponent(clientId)}`
    );
  }

  static getDevelopmentNotesByProject(projectId) {
    return this.request(
      `api/development/notes/project/${encodeURIComponent(projectId)}`
    );
  }

  static createDevelopmentNote(data) {
    return this.request(
      "api/development/notes",
      data,
      "post"
    );
  }

  static updateDevelopmentNote(id, data) {
    return this.request(
      `api/development/notes/${encodeURIComponent(id)}`,
      data,
      "patch"
    );
  }

  static deleteDevelopmentNote(id) {
    return this.request(
      `api/development/notes/${encodeURIComponent(id)}`,
      {},
      "delete"
    );
  }

  /* -----------------------------------------
     Development Proposals
  ------------------------------------------ */

  static getDevelopmentProposals(params = {}) {
    return this.request(
      "api/development/proposals",
      params
    );
  }

  static getDevelopmentProposal(id) {
    return this.request(
      `api/development/proposals/${encodeURIComponent(id)}`
    );
  }

  static getDevelopmentProposalsByClient(clientId) {
    return this.request(
      `api/development/proposals/client/${encodeURIComponent(clientId)}`
    );
  }

  static createDevelopmentProposal(data) {
    return this.request(
      "api/development/proposals",
      data,
      "post"
    );
  }

  static updateDevelopmentProposal(id, data) {
    return this.request(
      `api/development/proposals/${encodeURIComponent(id)}`,
      data,
      "patch"
    );
  }

  static deleteDevelopmentProposal(id) {
    return this.request(
      `api/development/proposals/${encodeURIComponent(id)}`,
      {},
      "delete"
    );
  }

  /* -----------------------------------------
     Development Proposal Items
  ------------------------------------------ */

  static getDevelopmentProposalItems(proposalId) {
    return this.request(
      `api/development/proposal-items/proposal/${encodeURIComponent(proposalId)}`
    );
  }

  static createDevelopmentProposalItem(proposalId, data) {
    return this.request(
      `api/development/proposal-items/proposal/${encodeURIComponent(proposalId)}`,
      data,
      "post"
    );
  }

  static updateDevelopmentProposalItem(id, data) {
    return this.request(
      `api/development/proposal-items/${encodeURIComponent(id)}`,
      data,
      "patch"
    );
  }

  static deleteDevelopmentProposalItem(id) {
    return this.request(
      `api/development/proposal-items/${encodeURIComponent(id)}`,
      {},
      "delete"
    );
  }

  /* -----------------------------------------
     Development Invoices
  ------------------------------------------ */

  static getDevelopmentInvoices(params = {}) {
    return this.request(
      "api/development/invoices",
      params
    );
  }

  static getDevelopmentInvoice(id) {
    return this.request(
      `api/development/invoices/${encodeURIComponent(id)}`
    );
  }

  static getDevelopmentInvoicesByProject(projectId) {
    return this.request(
      `api/development/invoices/project/${encodeURIComponent(projectId)}`
    );
  }

  static createDevelopmentInvoice(data) {
    return this.request(
      "api/development/invoices",
      data,
      "post"
    );
  }

  static updateDevelopmentInvoice(id, data) {
    return this.request(
      `api/development/invoices/${encodeURIComponent(id)}`,
      data,
      "patch"
    );
  }

  static deleteDevelopmentInvoice(id) {
    return this.request(
      `api/development/invoices/${encodeURIComponent(id)}`,
      {},
      "delete"
    );
  }

  /* -----------------------------------------
     Development Invoice Items
  ------------------------------------------ */

  static getDevelopmentInvoiceItems(invoiceId) {
    return this.request(
      `api/development/invoice-items/invoice/${encodeURIComponent(invoiceId)}`
    );
  }

  static createDevelopmentInvoiceItem(invoiceId, data) {
    return this.request(
      `api/development/invoice-items/invoice/${encodeURIComponent(invoiceId)}`,
      data,
      "post"
    );
  }

  static updateDevelopmentInvoiceItem(id, data) {
    return this.request(
      `api/development/invoice-items/${encodeURIComponent(id)}`,
      data,
      "patch"
    );
  }

  static deleteDevelopmentInvoiceItem(id) {
    return this.request(
      `api/development/invoice-items/${encodeURIComponent(id)}`,
      {},
      "delete"
    );
  }

  /* -----------------------------------------
     Development Payments
  ------------------------------------------ */

  static getDevelopmentPayments(params = {}) {
    return this.request(
      "api/development/payments",
      params
    );
  }

  static getDevelopmentPayment(id) {
    return this.request(
      `api/development/payments/${encodeURIComponent(id)}`
    );
  }

  static createDevelopmentPayment(data) {
    return this.request(
      "api/development/payments",
      data,
      "post"
    );
  }

  static updateDevelopmentPayment(id, data) {
    return this.request(
      `api/development/payments/${encodeURIComponent(id)}`,
      data,
      "patch"
    );
  }

  static deleteDevelopmentPayment(id) {
    return this.request(
      `api/development/payments/${encodeURIComponent(id)}`,
      {},
      "delete"
    );
  }

  /* -----------------------------------------
     Development Document Templates
  ------------------------------------------ */

  static getDevelopmentDocumentTemplates(params = {}) {
    return this.request(
      "api/development/document-templates",
      params
    );
  }

  static getDevelopmentDocumentTemplate(id) {
    return this.request(
      `api/development/document-templates/${encodeURIComponent(id)}`
    );
  }

  static createDevelopmentDocumentTemplate(data) {
    return this.request(
      "api/development/document-templates",
      data,
      "post"
    );
  }

  static updateDevelopmentDocumentTemplate(id, data) {
    return this.request(
      `api/development/document-templates/${encodeURIComponent(id)}`,
      data,
      "patch"
    );
  }

  static deleteDevelopmentDocumentTemplate(id) {
    return this.request(
      `api/development/document-templates/${encodeURIComponent(id)}`,
      {},
      "delete"
    );
  }

// =========================
// Development Invoice Item Catalog
// =========================

static getDevelopmentInvoiceItemCatalog(
  activeOnly = false
) {
  const query = activeOnly
    ? "?active=true"
    : "";

  return this.request(
    `api/development/invoice-item-catalog${query}`
  );
}

static getDevelopmentInvoiceItemCatalogItem(
  id
) {
  return this.request(
    `api/development/invoice-item-catalog/${encodeURIComponent(id)}`
  );
}

static createDevelopmentInvoiceItemCatalogItem(
  data
) {
  return this.request(
    "api/development/invoice-item-catalog",
    data,
    "post"
  );
}

static updateDevelopmentInvoiceItemCatalogItem(
  id,
  data
) {
  return this.request(
    `api/development/invoice-item-catalog/${encodeURIComponent(id)}`,
    data,
    "patch"
  );
}

static setDevelopmentInvoiceItemCatalogStatus(
  id,
  active
) {
  return this.request(
    `api/development/invoice-item-catalog/${encodeURIComponent(id)}/status`,
    {
      active,
    },
    "patch"
  );
}

static deleteDevelopmentInvoiceItemCatalogItem(
  id
) {
  return this.request(
    `api/development/invoice-item-catalog/${encodeURIComponent(id)}`,
    {},
    "delete"
  );
}

  /* -----------------------------------------
     Development Documents
  ------------------------------------------ */

  static getDevelopmentDocuments(params = {}) {
    return this.request(
      "api/development/documents",
      params
    );
  }

  static getDevelopmentDocument(id) {
    return this.request(
      `api/development/documents/${encodeURIComponent(id)}`
    );
  }

  static createDevelopmentDocument(data) {
    return this.request(
      "api/development/documents",
      data,
      "post"
    );
  }

  static updateDevelopmentDocument(id, data) {
    return this.request(
      `api/development/documents/${encodeURIComponent(id)}`,
      data,
      "patch"
    );
  }

  static deleteDevelopmentDocument(id) {
    return this.request(
      `api/development/documents/${encodeURIComponent(id)}`,
      {},
      "delete"
    );
  }

  static async requestBlob(
    endpoint,
    params = {}
  ) {
    const normalizedEndpoint =
      String(endpoint).replace(
        /^\/+/,
        ""
      );

    const url =
      `${BASE_URL}/${normalizedEndpoint}`;

    const headers = this.token
      ? {
          Authorization:
            `Dragonian ${this.token}`,
        }
      : {};

    try {
      const response =
        await axios({
          url,
          method: "get",
          headers,
          params,
          responseType: "blob",
        });

      return response.data;
    } catch (error) {
      let message =
        error?.message ||
        "The file could not be retrieved.";

      const responseData =
        error?.response?.data;

      if (
        responseData instanceof Blob
      ) {
        try {
          const responseText =
            await responseData.text();

          const parsed =
            JSON.parse(responseText);

          message =
            parsed?.error?.message ||
            parsed?.error ||
            parsed?.message ||
            message;
        } catch (parseError) {
          console.error(
            "Unable to read Blob error response:",
            parseError
          );
        }
      }

      const requestError =
        new Error(message);

      requestError.status =
        error?.response?.status;

      throw requestError;
    }
  }

  /* -----------------------------------------
    Development Document PDFs
  ------------------------------------------ */

  static getDevelopmentDocumentPdfMetadata(
    documentId
  ) {
    return this.request(
      `api/development/document-pdf/${encodeURIComponent(
        documentId
      )}`
    );
  }

  static saveDevelopmentDocumentPdf(
    documentId,
    data
  ) {
    return this.request(
      `api/development/document-pdf/${encodeURIComponent(
        documentId
      )}`,
      data,
      "post"
    );
  }

  static getDevelopmentDocumentPdfBlob(
    documentId,
    download = false
  ) {
    const action =
      download
        ? "download"
        : "view";

    return this.requestBlob(
      `api/development/document-pdf/${encodeURIComponent(
        documentId
      )}/${action}`
    );
  }

  static deleteDevelopmentDocumentPdf(
    documentId
  ) {
    return this.request(
      `api/development/document-pdf/${encodeURIComponent(
        documentId
      )}`,
      {},
      "delete"
    );
  }

  /* -----------------------------------------
     Development Project Files
  ------------------------------------------ */

  static getDevelopmentProjectFiles(params = {}) {
    return this.request(
      "api/development/project-files",
      params
    );
  }

  static getDevelopmentProjectFile(id) {
    return this.request(
      `api/development/project-files/${encodeURIComponent(id)}`
    );
  }

  static getDevelopmentProjectFilesByProject(projectId) {
    return this.request(
      `api/development/project-files/project/${encodeURIComponent(projectId)}`
    );
  }

  static createDevelopmentProjectFile(data) {
    return this.request(
      "api/development/project-files",
      data,
      "post"
    );
  }

  static updateDevelopmentProjectFile(id, data) {
    return this.request(
      `api/development/project-files/${encodeURIComponent(id)}`,
      data,
      "patch"
    );
  }

  static deleteDevelopmentProjectFile(id) {
    return this.request(
      `api/development/project-files/${encodeURIComponent(id)}`,
      {},
      "delete"
    );
  }

  /* -----------------------------------------
     Development Portfolio Projects
  ------------------------------------------ */

  static getDevelopmentPortfolioProjects(params = {}) {
    return this.request(
      "api/development/portfolio-projects",
      params
    );
  }

  static getDevelopmentPortfolioProject(id) {
    return this.request(
      `api/development/portfolio-projects/${encodeURIComponent(id)}`
    );
  }

  static getDevelopmentPortfolioProjectBySlug(slug) {
    return this.request(
      `api/development/portfolio-projects/slug/${encodeURIComponent(slug)}`
    );
  }

  static createDevelopmentPortfolioProject(data) {
    return this.request(
      "api/development/portfolio-projects",
      data,
      "post"
    );
  }

  static updateDevelopmentPortfolioProject(id, data) {
    return this.request(
      `api/development/portfolio-projects/${encodeURIComponent(id)}`,
      data,
      "patch"
    );
  }

  static deleteDevelopmentPortfolioProject(id) {
    return this.request(
      `api/development/portfolio-projects/${encodeURIComponent(id)}`,
      {},
      "delete"
    );
  }

  /* -----------------------------------------
     Development Settings
  ------------------------------------------ */

  static getDevelopmentSettings() {
    return this.request(
      "api/development/settings"
    );
  }

  static getDevelopmentSetting(id) {
    return this.request(
      `api/development/settings/${encodeURIComponent(id)}`
    );
  }

  static getDevelopmentSettingByKey(settingKey) {
    return this.request(
      `api/development/settings/key/${encodeURIComponent(settingKey)}`
    );
  }

  static createDevelopmentSetting(data) {
    return this.request(
      "api/development/settings",
      data,
      "post"
    );
  }

  static saveDevelopmentSettingByKey(settingKey, data) {
    return this.request(
      `api/development/settings/key/${encodeURIComponent(settingKey)}`,
      data,
      "put"
    );
  }

  static updateDevelopmentSetting(id, data) {
    return this.request(
      `api/development/settings/${encodeURIComponent(id)}`,
      data,
      "patch"
    );
  }

  static deleteDevelopmentSetting(id) {
    return this.request(
      `api/development/settings/${encodeURIComponent(id)}`,
      {},
      "delete"
    );
  }
  
  // =========================
  // ✅ 4H Methods
  // =========================

  static async getAll(route) {
    try {
      const resources = await this.request(`fourh/${route}`);
      return resources;
    } catch (e) {
      return { "error": "This is not available at this time. Please check back later." };
    }
  }

  static async getUserAssessment(uid, tid) {
    try {
      const resources = await this.request(`fourh/assessment/${uid}/${tid}`);
      if (resources.length > 0) return { "error": "User has already taken test." };
      else return { "proceed": "User has not taken test." };
    } catch (err) {
      return { "error": "There was an error." };
    }
  }

  static async getPdf(uid, tid) {
    try {
      const resources = await this.request(`fourh/pdf/${uid}/${tid}`);
      return resources;
    } catch (err) {
      return { "error": `There is no test PDF for ${uid}.` };
    }
  }

  static async getTestQuestions(id) {
    try {
      const resources = await this.request(`fourh/questions/${id}`);
      return resources;
    } catch (e) {
      return { "error": "No test with that ID." };
    }
  }

  static async getUserById(id) {
    try {
      const resources = await this.request(`fourh/users/${id}`);
      return resources;
    } catch (e) {
      return { "error": "User Not Found" };
    }
  }

  static async addUserAssessment(userId, testId, answers, completed, readyForCoordinator, moderatorId, readyToSendToLeader, sent, knowledgeScore, identifyScore) {
    try {
      const resources = await this.request("fourh/assessment", {
        userId,
        testId,
        answers,
        completed,
        readyForCoordinator,
        moderatorId: 0,
        readyToSendToLeader,
        sent,
        knowledgeScore,
        identifyScore
      }, "POST");
      return resources;
    } catch (e) {
      return { "error": "User has already added their assessment." };
    }
  }

  static async addUserPdf({ userId, testId, page_one, page_two, team }) {
    try {
      const resources = await this.request("fourh/pdf", {
        userId, testId, page_one, page_two, team
      }, "POST");
      return resources;
    } catch (error) {
      return { "error": "User's PDF has already been added to the system." };
    }
  }

  static async addUser(userData) {
    try {
      const res = await this.request("fourh/users", {
        fourHId: userData.fourHId,
        password: "test",
        nameLast: userData.nameLast,
        nameFirst: userData.nameFirst,
        nameMiddle: userData.nameMiddle,
        team: userData.team,
        email: userData.email,
        level: userData.level,
        admin: userData.admin,
        participant: userData.participant,
        leader: userData.leader,
        coordinator: userData.coordinator,
        image: ""
      }, "POST");
      return res;
    } catch (err) {
      return { "error": "User already exists with ID." };
    }
  }

  static async sendEmail(emailData) {
    try {
      const resources = await this.request("fourh/send-email", emailData, "POST");
      return resources;
    } catch (e) {
      return { "error": "Failed to send email." };
    }
  }

  static emailDevelopmentDocument(id, data) {
    return this.request(
      `api/development/documents/${encodeURIComponent(id)}/email`,
      data,
      "post"
    );
  }

  static getDevelopmentDocumentEmailHistory(id) {
    return this.request(
      `api/development/documents/${encodeURIComponent(id)}/email-history`
    );
  }

  static async updateUserAssessment(uid, tid, updates) {
    try {
      await this.request(`fourh/assessment/${uid}/${tid}`, updates, "PATCH");
      return { "success": `Updated user ${uid}'s assessment for test ID ${tid}.` };
    } catch (e) {
      return { "error": "User has not taken the test." };
    }
  }

  static async updateQuestion(tid, qid, level, updates) {
    try {
      await this.request(`fourh/questions/${tid}/${qid}/${level}`, updates, "PATCH");
      return { "Success": `Updated the ${level} Question ${qid} for Test ${tid}.` };
    } catch (e) {
      return { "error": "Question does not exist on test." };
    }
  }

static async addTest(testData) {
  try {
    const resources = await this.request("fourh/test", {
      title: testData.title
    }, "POST");
    return resources;
  } catch (e) {
    return { "error": "Test could not be added." };
  }
}

  static async updateTest(tid, title) {
    try {
      await this.request(`fourh/test/${tid}`, title, "PATCH");
      return { "Success": `Updated test ${tid} title to: ${title}` };
    } catch (e) {
      return { "Error": `No test with ID ${tid}.` };
    }
  }

  static async updateUser(uid, data) {
    try {
      await this.request(`fourh/users/${uid}`, data, "PATCH");
      return { "Success": `Updated user ${uid}.` };
    } catch (e) {
      return { "Error": `No user with ID ${uid}` };
    }
  }

  static async deleteSingle(route, id1, id2) {
    try {
      const ids = `${id1}${id2 !== "" ? `/${id2}` : ""}`;
      await this.request(`fourh/${route}/${ids}`, {}, "DELETE");
      return { "success": "Deleted" };
    } catch (e) {
      return { "error": "ID/s do not match, try again." };
    }
  }

  static async lockAssessment(userId, testerId, testId) {
    try {
      const resources = await this.request("fourh/lock/lock", { userId, testerId, testId }, "POST");
      return resources;
    } catch (e) {
      return { "error": "Failed to lock the assessment." };
    }
  }

  static async unlockAssessment(userId, testId) {
    try {
      const resources = await this.request("fourh/lock/unlock", { userId, testId }, "POST");
      return resources;
    } catch (e) {
      return { "error": "Failed to unlock the assessment." };
    }
  }

  static async isLocked(userId, testId) {
    try {
      const resources = await this.request(`fourh/lock/locked/${userId}/${testId}`);
      return resources;
    } catch (e) {
      return { "error": "User ID or Test ID are incorrect." };
    }
  }
}

const savedToken = getStoredValue("token");

if (savedToken) {
  Api.setToken(savedToken);
}

export default Api;
